// src/pages/StudentsPage.js
import React, { useEffect } from 'react';
import { Box, Typography, Button, Stack, TextField, Paper, Switch,
  FormControlLabel,
  Grid,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
  Divider
 } from '@mui/material';
import {
  Add as AddIcon,
  Print as PrintIcon,
  FileDownload as FileDownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
    FilterList as FilterListIcon,
  MoneyOff as MoneyOffIcon,
  CheckCircle as CheckCircleIcon,
  DirectionsBus as DirectionsBusIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux'; // useDispatch and useSelector
import { fetchStudentsPageData, setFilterValues, clearArchiveError } from '../slices/studentsSlice'; // UPDATED import
import DynamicDataGrid from '../components/DynamicDataGrid';
import ArchiveIcon from '@mui/icons-material/Archive';
import { archiveStudent } from '../slices/studentsSlice'; 
import { useToast } from '../context/ToastContext';
import SchoolIcon from '@mui/icons-material/School';
import { alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { motion } from 'framer-motion';
import { exportToExcel, exportToPrintWindow } from '../utils/exportUtils';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { isTeacher, hasPermission, TEACHER_PERMISSIONS } from '../config/teacherPermissions';

const MotionPaper = motion(Paper);

const StudentsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isTeacherUser = isTeacher();
  const hideFinancial = isTeacherUser && TEACHER_PERMISSIONS.students.hideFinancialFilters;
  const canCreate = !isTeacherUser || TEACHER_PERMISSIONS.students.canCreate;
  const canEdit = !isTeacherUser || TEACHER_PERMISSIONS.students.canEdit;
  const canArchive = !isTeacherUser || TEACHER_PERMISSIONS.students.canArchive;
  
  const [onlyUnpaid, setOnlyUnpaid] = React.useState(false);
  const [clothesFeePaid, setClothesFeePaid] = React.useState(false);
  const [hasUnreceivedPayments, setHasUnreceivedPayments] = React.useState(false);
  const [isBusJoined, setIsBusJoined] = React.useState(false); 
  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    studentId: null,
    studentName: ''
  });
  
  const { 
    filteredStudents, 
    filterValues, 
    status, 
    archiveStatus, 
    archiveError 
  } = useSelector((state) => state.students);
  const { showToast } = useToast();

  // ✅ NEW: Extract unique classes from student data
  const uniqueClasses = React.useMemo(() => {
    if (!filteredStudents) return [];
    
    const classMap = new Map();
    
    filteredStudents.forEach(student => {
      if (student.school_class && student.school_class_name) {
        classMap.set(student.school_class, {
          id: student.school_class,
          name: student.school_class_name
        });
      }
    });
    
    return Array.from(classMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'ar'));
  }, [filteredStudents]);

  useEffect(() => {
    // ✅ REMOVED: dispatch(fetchClasses());
    dispatch(fetchStudentsPageData({ is_archived: false }));
  }, [dispatch]);

  // Handle archive errors
  useEffect(() => {
    if (archiveStatus === 'failed' && archiveError) {
      showToast(archiveError, 'error');
      dispatch(clearArchiveError());
    }
  }, [archiveStatus, archiveError, showToast, dispatch]);

  const handleExportExcel = () => {
  const baseColumns = [
    { header: 'رقم الهوية', key: 'student_id' },
    { header: 'الاسم', key: 'first_name' },
    { header: 'اسم العائلة', key: 'second_name' },
    { header: 'الصف', key: 'school_class' },
    { header: 'العنوان', key: 'address' },
    { header: 'الهاتف', key: 'parent_phone' },
    { header: 'اسم ولي الأمر', key: 'parent_name' },
    { header: 'تاريخ التسجيل', key: 'date_of_registration' },
    { header: 'الحالة', key: 'status' },
  ];
  
  const financialColumns = hideFinancial ? [] : [
    { header: 'المدفوع', key: 'total_received' },
    { header: 'الرسوم', key: 'total_fee' },
    { header: 'المتبقي', key: 'remaining' },
    { header: 'رسوم الملابس مدفوعة', key: 'clothes_fee_paid' },
    { header: 'مشترك في المواصلات', key: 'is_bus_joined' },
  ];

  const columns = [...baseColumns, ...financialColumns];

  const data = finalFilteredStudents.map(s => {
    const baseData = {
      student_id: s.student_id,
      first_name: s.first_name,
      second_name: s.second_name,
      school_class: s.school_class_name || 'غير معروف',
      address: s.address || 'فلسطين',
      parent_phone: s.parent_phone,
      parent_name: s.parent_name,
      date_of_registration: s.date_of_registration,
      status: s.status,
    };

    if (!hideFinancial) {
      const totalFee = s.payment_summary?.total_fee || 0;
      const totalReceived = s.payment_summary?.total_received || 0;
      const remaining = s.payment_summary?.remaining_combined
        ?? (totalFee - totalReceived);

      return {
        ...baseData,
        total_received: totalReceived,
        total_fee: totalFee,
        remaining,
        clothes_fee_paid: s?.clothes_fee_paid ? 'نعم' : 'لا',
        is_bus_joined: s.is_bus_joined ? 'نعم' : 'لا',
      };
    }

    return baseData;
  });

  exportToExcel(data, columns, 'students');
};

  const handlePrint = async () => {
  const baseColumns = [
    { header: 'رقم الهوية', key: 'student_id' },
    { header: 'الاسم', key: 'first_name' },
    { header: 'اسم العائلة', key: 'second_name' },
    { header: 'الصف', key: 'school_class' },
    { header: 'العنوان', key: 'address' },
  ];
  
  const financialColumns = hideFinancial ? [] : [
    { header: 'المدفوع', key: 'total_received' },
    { header: 'الرسوم', key: 'total_fee' },
    { header: 'المتبقي', key: 'remaining' },
    { header: 'رسوم الملابس', key: 'clothes_fee_paid' },
    { header: 'المواصلات', key: 'is_bus_joined' },
  ];

  const columns = [...baseColumns, ...financialColumns];

  const data = finalFilteredStudents.map(s => {
    const baseData = {
      student_id: s.student_id,
      first_name: s.first_name,
      second_name: s.second_name,
      school_class: s.school_class_name || 'غير معروف',
      address: s.address || 'فلسطين',
    };

    if (!hideFinancial) {
      const totalFee = s.payment_summary?.total_fee || 0;
      const totalReceived = s.payment_summary?.total_received || 0;
      const remaining = s.payment_summary?.remaining_combined
        ?? (totalFee - totalReceived);

      return {
        ...baseData,
        total_received: totalReceived,
        total_fee: totalFee,
        remaining,
        clothes_fee_paid: s?.clothes_fee_paid ? 'نعم' : 'لا',
        is_bus_joined: s.is_bus_joined ? 'نعم' : 'لا',
      };
    }

    return baseData;
  });

  await exportToPrintWindow(data, columns, 'قائمة الطلاب');
};

     const enhancedFiltersSection = (
    <MotionPaper
      component={motion.div}
      elevation={0}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
      sx={{
        mb: 3,
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        bgcolor: '#fff',
        boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
        border: '1px solid #E5E7EB',
        fontFamily: 'Cairo, Tajawal, sans-serif',
      }}
    >
      {/* Export Buttons Row */}
      <Stack useFlexGap
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: '#1E1B4B',
            fontFamily: 'Cairo, Tajawal, sans-serif',
          }}
        >
          تصدير القائمة
        </Typography>
        <Stack useFlexGap
          direction="row"
          spacing={1.5}
          sx={{ flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'stretch', sm: 'flex-end' } }}
        >
          <Button
            variant="outlined"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#16A34A',
              color: '#16A34A',
              '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
            }}
          >
            تصدير Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handlePrint}
            sx={{
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              px: 2.5,
              fontFamily: 'Cairo, Tajawal, sans-serif',
              borderColor: '#4338CA',
              color: '#4338CA',
              '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
            }}
          >
            طباعة
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 2.5, borderColor: '#E5E7EB' }} />

      {/* Filters Section */}
      <Box>
        <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <FilterListIcon sx={{ color: '#4338CA', fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}
          >
            تصفية النتائج
          </Typography>
        </Stack>
        <Grid container spacing={2} alignItems="center">
          {/* Financial filters - hidden for teachers */}
          {!hideFinancial && (
            <>
              {/* Unpaid Students Filter — hidden */}

              {/* Clothes Fee Paid Filter */}
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={clothesFeePaid}
                      onChange={(e) => setClothesFeePaid(e.target.checked)}
                      color="success"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: clothesFeePaid ? '#16A34A' : '#9CA3AF' }} fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        دفع رسوم الملابس
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: clothesFeePaid ? '#DCFCE7' : '#F8F9FF',
                    borderRadius: 2.5,
                    px: 2,
                    py: 1,
                    border: clothesFeePaid ? '1.5px solid #16A34A' : '1.5px solid #E5E7EB',
                    transition: 'all 0.3s ease',
                    margin: 0,
                    width: '100%',
                    '&:hover': {
                      backgroundColor: clothesFeePaid ? '#DCFCE7' : '#EEF2FF'
                    }
                  }}
                />
              </Grid>

              {/* Bus Joined Filter */}
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isBusJoined}
                      onChange={(e) => setIsBusJoined(e.target.checked)}
                      color="info"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DirectionsBusIcon sx={{ color: isBusJoined ? '#0284C7' : '#9CA3AF' }} fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        مشترك في المواصلات
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: isBusJoined ? '#E0F2FE' : '#F8F9FF',
                    borderRadius: 2.5,
                    px: 2,
                    py: 1,
                    border: isBusJoined ? '1.5px solid #0284C7' : '1.5px solid #E5E7EB',
                    transition: 'all 0.3s ease',
                    margin: 0,
                    width: '100%',
                    '&:hover': {
                      backgroundColor: isBusJoined ? '#E0F2FE' : '#EEF2FF'
                    }
                  }}
                />
              </Grid>

              {/* Has Unreceived Payments Filter */}
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasUnreceivedPayments}
                      onChange={(e) => setHasUnreceivedPayments(e.target.checked)}
                      color="error"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MoneyOffIcon sx={{ color: hasUnreceivedPayments ? '#DC2626' : '#9CA3AF' }} fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E1B4B', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
                        لديهم مبالغ لم يتم تحصيلها
                      </Typography>
                    </Box>
                  }
                  sx={{
                    backgroundColor: hasUnreceivedPayments ? '#FEE2E2' : '#F8F9FF',
                    borderRadius: 2.5,
                    px: 2,
                    py: 1,
                    border: hasUnreceivedPayments ? '1.5px solid #DC2626' : '1.5px solid #E5E7EB',
                    transition: 'all 0.3s ease',
                    margin: 0,
                    width: '100%',
                    '&:hover': {
                      backgroundColor: hasUnreceivedPayments ? '#FEE2E2' : '#EEF2FF'
                    }
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>

        {/* Active Filters Summary - only show if filters are visible */}
        {!hideFinancial && (onlyUnpaid || clothesFeePaid || isBusJoined || hasUnreceivedPayments) && (
          <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: '#EEF2FF' }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: '#4338CA', fontFamily: 'Cairo, Tajawal, sans-serif' }}>
              الفلاتر المفعلة:
            </Typography>
            <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
              {onlyUnpaid && (
                <Chip
                  label="الطلاب غير المسددين"
                  color="warning"
                  size="small"
                  onDelete={() => {
                    setOnlyUnpaid(false);
                    dispatch(setFilterValues({ ...filterValues, onlyUnpaid: false }));
                  }}
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {hasUnreceivedPayments && (
                <Chip
                  label="لديهم مبالغ لم يتم تحصيلها"
                  color="error"
                  size="small"
                  onDelete={() => setHasUnreceivedPayments(false)}
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {clothesFeePaid && (
                <Chip
                  label="دفع رسوم الملابس"
                  color="success"
                  size="small"
                  onDelete={() => setClothesFeePaid(false)}
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {isBusJoined && (
                <Chip
                  label="مشترك في المواصلات"
                  color="info"
                  size="small"
                  onDelete={() => setIsBusJoined(false)}
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </MotionPaper>
  );

    const finalFilteredStudents = React.useMemo(() => {
  if (!filteredStudents) return [];
  
  return filteredStudents.filter(student => {
    // Clothes fee paid filter
    const matchesClothesFeePaid = !clothesFeePaid || 
      student?.clothes_fee_paid === true;

    // Bus joined filter  
    const matchesBusJoined = !isBusJoined || 
      student.is_bus_joined === true;

    // Has unreceived payments filter — aligned with المتبقي (total_fee - total_received)
    const matchesUnreceivedPayments = !hasUnreceivedPayments || (() => {
      const remaining = student.payment_summary?.remaining_combined
        ?? ((student.payment_summary?.total_fee || 0) - (student.payment_summary?.total_received || 0));
      return remaining > 0;
    })();

    return matchesClothesFeePaid && matchesBusJoined && matchesUnreceivedPayments;
  });
}, [filteredStudents, clothesFeePaid, isBusJoined, hasUnreceivedPayments]);

  const handleUnpaidChange = (checked) => {
  setOnlyUnpaid(checked);
  dispatch(setFilterValues({ ...filterValues, onlyUnpaid: checked }));
  // Trigger API call
  dispatch(fetchStudentsPageData({ 
    is_archived: false, 
    ...filterValues, 
    onlyUnpaid: checked 
  }));
};

  
  const handleFilterChange = (field, value) => {
    dispatch(setFilterValues({ ...filterValues, [field]: value }));
  };

  const handleAddStudent = () => {
    navigate('/students/create');
  };

  const handleArchive = async (id) => {
    try {
      const result = await dispatch(archiveStudent(id)).unwrap();
      showToast('تمت أرشفة الطالب بنجاح', 'success');
      setConfirmDialog({ open: false, studentId: null, studentName: '' });
    } catch (error) {
      // Error is already handled by useEffect above
      console.error('Archive error:', error);
      setConfirmDialog({ open: false, studentId: null, studentName: '' });
    }
  };

  const openArchiveDialog = (student) => {
    const studentName = `${student.first_name} ${student.second_name || ''}`.trim();
    setConfirmDialog({
      open: true,
      studentId: student.id,
      studentName
    });
  };

  const closeArchiveDialog = () => {
    setConfirmDialog({ open: false, studentId: null, studentName: '' });
  };

  // Base columns (always visible)
  const baseColumns = [
    {
      field: 'student_id',
      headerName: 'رقم الهوية',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">رقم الهوية</Typography>
          <TextField
            variant="standard"
            value={filterValues.id || ''}
            onChange={(e) => handleFilterChange('id', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{
              disableUnderline: true,
              style: { fontSize: '0.75rem', textAlign: 'center' },
            }}
          />
        </Box>
      )
    },
    {
      field: 'first_name',
      headerName: 'الاسم',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">الاسم</Typography>
          <TextField
            variant="standard"
            value={filterValues.first_name || ''}
            onChange={(e) => handleFilterChange('first_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{
              disableUnderline: true,
              style: { fontSize: '0.75rem', textAlign: 'center' },
            }}
          />
        </Box>
      )
    },
    {
      field: 'second_name',
      headerName: 'اسم العائلة',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography fontWeight="bold">اسم العائلة</Typography>
          <TextField
            variant="standard"
            value={filterValues.second_name || ''}
            onChange={(e) => handleFilterChange('second_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={{
              disableUnderline: true,
              style: { fontSize: '0.75rem', textAlign: 'center' },
            }}
          />
        </Box>
      )
    },
    {
      field: 'school_class',
      headerName: 'الصف',
      flex: 1,
      valueGetter: (params) => params?.row?.school_class_name || 'غير معروف', // ✅ UPDATED: Use school_class_name directly
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.school_class_name || 'غير معروف'} {/* ✅ UPDATED: Use school_class_name directly */}
        </Typography>
      ),
      renderHeader: () => (
  <Box>
    <Typography fontWeight="bold">الصف</Typography>
    <TextField
      select
      variant="standard"
      value={filterValues.school_class || ''}
      onChange={(e) => handleFilterChange('school_class', e.target.value)}
      placeholder="بحث"
      size="small"
      SelectProps={{
        native: true,
      }}
      InputProps={{ 
        disableUnderline: true, 
        style: { fontSize: '0.75rem', textAlign: 'center' } 
      }}
    >
      <option value="">جميع الصفوف</option>
      {/* ✅ UPDATED: Use uniqueClasses instead of list */}
      {uniqueClasses.map((classItem) => (
        <option key={classItem.id} value={classItem.id}>
          {classItem.name}
        </option>
      ))}
    </TextField>
  </Box>
)
    },
    {
    field: 'address',
    headerName: 'العنوان',
    flex: 1,
    renderCell: (params) => (
      <Typography variant="body2">
        {params.row.address || 'فلسطين'}
      </Typography>
    ),
    renderHeader: () => (
      <Box>
        <Typography fontWeight="bold">العنوان</Typography>
        <TextField
          variant="standard"
          value={filterValues.address || ''}
          onChange={(e) => handleFilterChange('address', e.target.value)}
          placeholder="بحث"
          size="small"
          InputProps={{
            disableUnderline: true,
            style: { fontSize: '0.75rem', textAlign: 'center' },
          }}
        />
      </Box>
    )
  },
  ];
  
  // Financial columns (hidden for teachers)
  const financialColumns = hideFinancial ? [] : [
    {
      field: 'payment_summary.total_received',
      headerName: 'المدفوع',
      flex: 1,
      renderCell: (params) => <Typography color="success.main">{params.row.payment_summary?.total_received || params.row.paid_sum || 0} شيكل</Typography>,
      renderHeader: () => (
        <Typography fontWeight="bold">المدفوع</Typography>
      )
    },
    {
      field: 'payment_summary.total_fee',
      headerName: 'الرسوم',
      flex: 1,
      renderCell: (params) => <Typography color="primary.main">{params.row.payment_summary?.total_fee || params.row.total_fees || 0} شيكل</Typography>,
      renderHeader: () => (
        <Typography fontWeight="bold">الرسوم</Typography>
      )
    },
    {
      field: 'remaining_amount',
      headerName: 'المتبقي',
      flex: 1,
      renderCell: (params) => {
        const remaining = params.row.payment_summary?.remaining_combined
          ?? ((params.row.payment_summary?.total_fee || 0) - (params.row.payment_summary?.total_received || 0));

        return (
          <Typography
            color={remaining > 0 ? "error.main" : "success.main"}
            fontWeight="500"
          >
            {Number(remaining).toFixed(2)} شيكل
          </Typography>
        );
      },
      renderHeader: () => (
        <Typography fontWeight="bold">المتبقي</Typography>
      )
    },
  ];
  
  // Actions column
  const actionsColumn = {
    field: 'actions',
    headerName: 'إجراءات',
    flex: 1.5,
    sortable: false,
    renderCell: (params) => (
      <Stack useFlexGap direction="row" spacing={0.5}>
        <Tooltip title="عرض الطالب" arrow>
          <IconButton
            size="small"
            onClick={() => navigate(`/students/view/${params.row.id}`)}
            sx={{ color: '#4338CA', '&:hover': { bgcolor: '#EEF2FF' } }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {canEdit && (
          <Tooltip title="تعديل الطالب" arrow>
            <IconButton
              size="small"
              onClick={() => navigate(`/students/edit/${params.row.id}`)}
              sx={{ color: '#D97706', '&:hover': { bgcolor: '#FEF3C7' } }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canArchive && (
          <Tooltip title="أرشفة الطالب" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => openArchiveDialog(params.row)}
                disabled={archiveStatus === 'loading'}
                sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}
              >
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    )
  };
  
  // Combine columns based on permissions
  const columns = [...baseColumns, ...financialColumns, actionsColumn];
  

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header Section */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2.5,
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SchoolIcon sx={{ color: '#fff', fontSize: 30 }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              fontWeight={800}
              color="#fff"
              sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
            >
              إدارة الطلاب
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                mt: 0.25,
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              عرض وإدارة وتصدير بيانات الطلاب
            </Typography>
          </Box>
        </Stack>

        {canCreate && (
          <Button
            variant="contained"
            onClick={handleAddStudent}
            startIcon={<AddIcon />}
            data-guide-id="guide-add-student"
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة طالب
          </Button>
        )}
      </MotionPaper>

      {/* Actions */}
      {enhancedFiltersSection}

      {/* DataGrid Card */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        sx={{
          borderRadius: 3,
          p: { xs: 1, sm: 2 },
          bgcolor: '#fff',
          border: '1px solid #E5E7EB',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
        }}
      >
        <DynamicDataGrid
          columns={columns}
          rows={finalFilteredStudents}
          paginationProps={{
            labelRowsPerPage: 'عدد الصفوف في الصفحة:',
            labelDisplayedRows: ({ from, to, count }) =>
              `عرض ${from}–${to} من ${count !== -1 ? count : `أكثر من ${to}`}`,
            nextIconButtonProps: { 'aria-label': 'الصفحة التالية' },
            backIconButtonProps: { 'aria-label': 'الصفحة السابقة' },
            getItemAriaLabel: (type) => {
              if (type === 'first') return 'الصفحة الأولى';
              if (type === 'last') return 'الصفحة الأخيرة';
              if (type === 'next') return 'الصفحة التالية';
              if (type === 'previous') return 'الصفحة السابقة';
              return '';
            },
            dir: 'rtl',
            sx: {
              direction: 'rtl',
              '.MuiTablePagination-toolbar': {
                flexDirection: 'row-reverse',
                justifyContent: 'flex-start',
              },
              '.MuiTablePagination-actions': {
                marginLeft: 0,
                marginRight: '16px',
              },
            }
          }}
        />
      </MotionPaper>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={closeArchiveDialog}
        onConfirm={() => handleArchive(confirmDialog.studentId)}
        title="تأكيد أرشفة الطالب"
        message={`هل أنت متأكد من أرشفة الطالب "${confirmDialog.studentName}"؟ سيتم نقل الطالب إلى قائمة المؤرشفين.`}
        confirmText="أرشفة"
        cancelText="إلغاء"
        confirmColor="warning"
        loading={archiveStatus === 'loading'}
        showWarningIcon={true}
      />

      {status === 'loading' && (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      flexDirection: 'column'
    }}
  >
    <CircularProgress size={60} sx={{ color: '#4338CA' }} />
    <Typography variant="h6" sx={{ mt: 2, color: '#1E1B4B', fontWeight: 700, fontFamily: 'Cairo, Tajawal, sans-serif' }}>
      جاري تحميل بيانات الطلاب...
    </Typography>
  </Box>
)}
      </Box>
    </Box>
  );
};

export default StudentsPage;